/**
 * UI Store - manages UI state like sidebar, modals, and flash messages
 * @module stores/starnexus/uiStore
 *
 */

import { create } from 'zustand';

// ============================================================================
// Flash Message Types
// ============================================================================

export interface FlashMessage {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  header: string;
  content?: string;
  dismissible?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

// ============================================================================
// Confirmation Modal Types
// ============================================================================

export interface ConfirmationModalConfig {
  visible: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'primary' | 'danger';
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
}

// ============================================================================
// Loading State Types
// ============================================================================

export interface LoadingState {
  [key: string]: boolean;
}

// ============================================================================
// Store Interface
// ============================================================================

interface UIState {
  sidebarOpen: boolean;
  activeModal: string | null;
  flashMessages: FlashMessage[];
  theme: 'light' | 'dark';
  loadingStates: LoadingState;
  confirmationModal: ConfirmationModalConfig;
  globalLoading: boolean;
}

interface UIActions {
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  openModal: (modalId: string) => void;
  closeModal: () => void;
  addFlashMessage: (message: Omit<FlashMessage, 'id'>) => void;
  removeFlashMessage: (id: string) => void;
  clearFlashMessages: () => void;
  setTheme: (theme: 'light' | 'dark') => void;
  toggleTheme: () => void;
  setLoading: (key: string, isLoading: boolean) => void;
  isLoading: (key: string) => boolean;
  setGlobalLoading: (isLoading: boolean) => void;
  showConfirmation: (config: Omit<ConfirmationModalConfig, 'visible'>) => void;
  hideConfirmation: () => void;
  showSuccess: (header: string, content?: string) => void;
  showError: (header: string, content?: string, retryAction?: () => void) => void;
  showWarning: (header: string, content?: string) => void;
  showInfo: (header: string, content?: string) => void;
}

type UIStore = UIState & UIActions;

// ============================================================================
// Helper to generate unique IDs
// ============================================================================

const generateId = (): string => {
  return `flash-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// ============================================================================
// Default Confirmation Modal State
// ============================================================================

const defaultConfirmationModal: ConfirmationModalConfig = {
  visible: false,
  title: '',
  message: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  variant: 'primary',
  onConfirm: () => {},
  onCancel: undefined
};

// ============================================================================
// UI Store
// ============================================================================

export const useUIStore = create<UIStore>((set, get) => ({
  // Initial state
  sidebarOpen: true,
  activeModal: null,
  flashMessages: [],
  theme: 'light',
  loadingStates: {},
  confirmationModal: defaultConfirmationModal,
  globalLoading: false,

  // Sidebar actions
  toggleSidebar: () => {
    set((state) => ({ sidebarOpen: !state.sidebarOpen }));
  },

  setSidebarOpen: (open: boolean) => {
    set({ sidebarOpen: open });
  },

  // Modal actions
  openModal: (modalId: string) => {
    set({ activeModal: modalId });
  },

  closeModal: () => {
    set({ activeModal: null });
  },

  // Flash message actions
  addFlashMessage: (message: Omit<FlashMessage, 'id'>) => {
    const id = generateId();
    const newMessage: FlashMessage = {
      ...message,
      id,
      dismissible: message.dismissible ?? true
    };

    set((state) => ({
      flashMessages: [...state.flashMessages, newMessage]
    }));

    // Auto-dismiss after 5 seconds for non-error messages
    if (message.type !== 'error') {
      setTimeout(() => {
        get().removeFlashMessage(id);
      }, 5000);
    }
  },

  removeFlashMessage: (id: string) => {
    set((state) => ({
      flashMessages: state.flashMessages.filter((msg) => msg.id !== id)
    }));
  },

  clearFlashMessages: () => {
    set({ flashMessages: [] });
  },

  // Theme actions
  setTheme: (theme: 'light' | 'dark') => {
    set({ theme });
    document.body.classList.remove('awsui-dark-mode', 'awsui-light-mode');
    document.body.classList.add(theme === 'dark' ? 'awsui-dark-mode' : 'awsui-light-mode');
  },

  toggleTheme: () => {
    const currentTheme = get().theme;
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';
    get().setTheme(newTheme);
  },

  // Loading state actions
  setLoading: (key: string, isLoading: boolean) => {
    set((state) => ({
      loadingStates: { ...state.loadingStates, [key]: isLoading }
    }));
  },

  isLoading: (key: string) => {
    return get().loadingStates[key] ?? false;
  },

  setGlobalLoading: (isLoading: boolean) => {
    set({ globalLoading: isLoading });
  },

  // Confirmation modal actions
  showConfirmation: (config: Omit<ConfirmationModalConfig, 'visible'>) => {
    set({
      confirmationModal: { ...defaultConfirmationModal, ...config, visible: true }
    });
  },

  hideConfirmation: () => {
    set({ confirmationModal: defaultConfirmationModal });
  },

  // Convenience methods for common flash messages
  showSuccess: (header: string, content?: string) => {
    get().addFlashMessage({ type: 'success', header, content, dismissible: true });
  },

  showError: (header: string, content?: string, retryAction?: () => void) => {
    get().addFlashMessage({
      type: 'error',
      header,
      content,
      dismissible: true,
      action: retryAction ? { label: 'Retry', onClick: retryAction } : undefined
    });
  },

  showWarning: (header: string, content?: string) => {
    get().addFlashMessage({ type: 'warning', header, content, dismissible: true });
  },

  showInfo: (header: string, content?: string) => {
    get().addFlashMessage({ type: 'info', header, content, dismissible: true });
  }
}));

// ============================================================================
// Selector Hooks
// ============================================================================

export const useSidebarOpen = () => useUIStore((state) => state.sidebarOpen);
export const useActiveModal = () => useUIStore((state) => state.activeModal);
export const useFlashMessages = () => useUIStore((state) => state.flashMessages);
export const useTheme = () => useUIStore((state) => state.theme);
export const useLoadingState = (key: string) => useUIStore((state) => state.loadingStates[key] ?? false);
export const useGlobalLoading = () => useUIStore((state) => state.globalLoading);
export const useConfirmationModal = () => useUIStore((state) => state.confirmationModal);
